/**
 * @fileoverview InstitutionCard Component
 * 
 * Displays a financial institution and its accounts in an accordion format
 * with automatic position loading on expansion. This component is part of the
 * Wealthica2 integration UI and follows established design patterns.
 * 
 * Key features:
 * - Displays institution logo, name, and total balance
 * - Shows accounts in an expandable accordion format
 * - Automatically loads positions when an account is expanded
 * - Shows loading states and position counts
 * - Renders positions in a tabbed table when account is expanded
 * 
 * @module InstitutionCard
 */

import React, { useState } from 'react';
import InstitutionAccountItem from './InstitutionAccountItem';
import InstitutionHeader from './InstitutionHeader';
import InstitutionActionsBar from './InstitutionActionsBar';
import InstitutionDisconnectDialog from './InstitutionDisconnectDialog';
import InstitutionAccountsSkeleton from './InstitutionAccountsSkeleton';
import { InstitutionGroup } from '@/providers/wealthica2/utils/display-utils';
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from './wealthica-accordion';
import { toast } from 'sonner';

/**
 * Represents an account with its associated positions data
 * @interface AccountWithPositions
 */
interface AccountWithPositions {
  /** Unique identifier for the account */
  id: string;
  /** Array of position objects for this account */
  positions: any[];
}

/**
 * Props for the InstitutionCard component
 * @interface InstitutionCardProps
 */
interface InstitutionCardProps {
  /** Institution group data containing institution details and accounts */
  institution: InstitutionGroup;
  /** Array of account IDs that are currently expanded in the UI */
  expandedAccounts: string[];
  /** Array of accounts with their loaded position data */
  accountsWithPositions: AccountWithPositions[];
  /** Record mapping account IDs to their loading state */
  loadingPositionsFor: Record<string, boolean>;
  /** When true, the whole institution is currently fetching positions */
  isInstitutionPositionsLoading?: boolean;
  /** When true, the institution is busy with any operation (fetch, persist, positions) */
  isInstitutionBusy?: boolean;
  /** Callback to update the expanded account IDs (single-root multiple accordion) */
  onExpandedChange: (expandedIds: string[]) => void;
  /** Callback function to load positions for a specific account */
  onLoadPositions: (accountId: string, institutionId?: string) => void;
  /** Callback function to handle disconnection of this institution */
  onDisconnect?: (institutionId: string) => void;
  /** Callback function to refresh data for this specific institution */
  onRefresh?: (institutionId: string) => void;
  /** Optional last accounts fetch timestamp for this institution */
  lastSyncAt?: Date | null;
  /** Optional: emit position when a row is clicked */
  onPositionClick?: (position: any) => void;
}

/**
 * Renders a card for a financial institution and its accounts
 * with expandable accounts showing position data
 * 
 * @param {object} props - Component props
 * @param {InstitutionGroup} props.institution - Institution data to display
 * @param {string[]} props.expandedAccounts - IDs of expanded accounts
 * @param {AccountWithPositions[]} props.accountsWithPositions - Accounts with position data
 * @param {Record<string, boolean>} props.loadingPositionsFor - Loading states by account ID
 * @param {Function} props.onToggleAccount - Callback when account expansion is toggled
 * @param {Function} props.onLoadPositions - Callback to load positions for an account
 * @returns {JSX.Element} Rendered institution card component
 */
export function InstitutionCard({
  institution,
  expandedAccounts,
  accountsWithPositions,
  loadingPositionsFor,
  isInstitutionPositionsLoading,
  isInstitutionBusy,
  onExpandedChange,
  onLoadPositions,
  onDisconnect,
  onRefresh,
  lastSyncAt,
  onPositionClick
}: InstitutionCardProps) {
  const [showDisconnectDialog, setShowDisconnectDialog] = useState(false);
  const [confirmDisconnect, setConfirmDisconnect] = useState(false);
  const [isDisconnecting, setIsDisconnecting] = useState(false);
  const [isRefreshing, setIsRefreshing] = useState(false);
  const [institutionOpen, setInstitutionOpen] = useState(false);
  // Using sonner toast for consistent terminal toasts across flows

  const handleDisconnectConfirm = async () => {
    if (!confirmDisconnect) return;
    
    setIsDisconnecting(true);
    try {
      await onDisconnect?.(institution.id || '');
      toast.success('Institution disconnected', {
        description: `Successfully removed connection for ${institution.name}`
      });
      setShowDisconnectDialog(false);
    } catch (error) {
      toast.error('Disconnect failed', {
        description: `Failed to disconnect ${institution.name}`
      });
    } finally {
      setIsDisconnecting(false);
      setConfirmDisconnect(false);
    }
  };

  const isAccountLoading = (accountId: string) => loadingPositionsFor[accountId] || false;
  const isLoadingForDisplay = (accountId: string) => !!isInstitutionPositionsLoading || isAccountLoading(accountId);
  
  const accountHasPositions = (accountId: string) => {
    const account = accountsWithPositions.find(a => a.id === accountId);
    return !!(account && account.positions && account.positions.length > 0);
  };
  
  // Use calculatedBalance from parent instead of calculating here
  const getAccountBalance = (account: any): number => {
    // If calculatedBalance is provided by parent (from position data), use it
    // Otherwise fall back to account.balance as fallback
    return account.calculatedBalance !== undefined ? account.calculatedBalance : (account.balance || 0);
  };

  return (
    <div className="mb-4">
      {/* Outer accordion for the institution */}
      <Accordion 
        type="single" 
        collapsible 
        className="w-full border rounded-lg shadow-sm overflow-hidden"
        value={institutionOpen ? 'institution' : ''}
        onValueChange={(value) => setInstitutionOpen(value === 'institution')}
      >
        <AccordionItem value="institution" className="border-0">
          {/* Institution header as accordion trigger */}
          <AccordionTrigger className="w-full py-3 px-4 bg-white hover:no-underline">
            <InstitutionHeader
              institution={institution}
              lastSyncAt={lastSyncAt}
              institutionOpen={institutionOpen}
              isInstitutionPositionsLoading={isInstitutionPositionsLoading}
            />
          </AccordionTrigger>
          
          {/* Institution content containing action buttons and account accordions */}
          <AccordionContent className="divide-y divide-gray-100 bg-white">
            {/* Action buttons - only visible when institution accordion is open */}
            <InstitutionActionsBar
              onRefresh={onRefresh ? async () => {
                if (isRefreshing) return;
                setIsRefreshing(true);
                try {
                  await onRefresh(institution.id || '');
                } finally {
                  setIsRefreshing(false);
                }
              } : undefined}
              onDisconnectClick={onDisconnect ? () => setShowDisconnectDialog(true) : undefined}
              isRefreshing={isRefreshing}
              isInstitutionBusy={isInstitutionBusy}
              isDisconnecting={isDisconnecting}
            />
            <Accordion
              type="multiple"
              value={expandedAccounts}
              onValueChange={(next) => {
                const nextList = Array.isArray(next) ? (next as string[]) : [];
                onExpandedChange(nextList);
              }}
              className="w-full"
            >
              {isInstitutionPositionsLoading ? (
                <InstitutionAccountsSkeleton count={Math.min(6, (institution.accounts?.length || 6))} />
              ) : (
              institution.accounts.map((account, index) => (
                <InstitutionAccountItem
                  key={account.id}
                  account={account}
                  index={index}
                  accountsWithPositions={accountsWithPositions}
                  isLoadingForDisplay={isLoadingForDisplay}
                  accountHasPositions={accountHasPositions}
                  getAccountBalance={getAccountBalance}
                  onPositionClick={onPositionClick}
                />
              ))
              )}
            </Accordion>
          </AccordionContent>
        </AccordionItem>
      </Accordion>

      {/* Disconnect confirmation dialog */}
      <InstitutionDisconnectDialog
        open={showDisconnectDialog}
        setOpen={setShowDisconnectDialog}
        confirmDisconnect={confirmDisconnect}
        setConfirmDisconnect={setConfirmDisconnect}
        isDisconnecting={isDisconnecting}
        onConfirm={handleDisconnectConfirm}
        institutionName={institution.name}
      />
    </div>
  );
}

export default InstitutionCard;
